import { useState } from 'react'
import * as changeCase from 'change-case'
import { parseTimestamp } from '@klear/time/boundary'
import { getPastTense } from '@klear/order-event/display'
import { dateFormattedOrDash, listItemDateEpochDay } from '@klear/order-event/list-display'
import { useOrderEventUpdates } from '@klear/ui/hooks/use-order-events'
import type { OrderEventUpdate } from '@klear/order-event/order-event-update'
import { documentChangeLabel, orderEventTypeLabel } from '@klear/order-event-ui/event-timeline-utils'
import { cn } from '@klear/ui/lib/utils'

type DateKind = 'effective' | 'recorded'

const DATE_KIND_OPTIONS: { value: DateKind; label: string; title: string }[] = [
  { value: 'effective', label: 'Effective', title: 'Date the change applies to' },
  { value: 'recorded', label: 'Recorded', title: 'Date the change was made' }
]

function recordedMs(update: OrderEventUpdate): number {
  return parseTimestamp(update.createdAt)?.valueOf() ?? 0
}

function compareAscending(kind: DateKind, a: OrderEventUpdate, b: OrderEventUpdate): number {
  const recorded = recordedMs(a) - recordedMs(b) || a.id - b.id
  if (kind === 'recorded') return recorded
  return listItemDateEpochDay(a.effectiveDate) - listItemDateEpochDay(b.effectiveDate) || recorded
}

export default function EventStatus({
  eventId,
  orderId,
  sortOrder = 'oldest',
  onSelectEvent
}: {
  eventId?: number
  orderId?: number
  sortOrder?: 'newest' | 'oldest'
  onSelectEvent?: (targetEventId: number) => void
}) {
  const { orderEventUpdates = [], isLoading } = useOrderEventUpdates(eventId, orderId)
  const [dateKind, setDateKind] = useState<DateKind>('effective')

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading history…</p>
  }

  if (orderEventUpdates.length === 0) {
    return <p className="text-sm text-muted-foreground">No activity yet</p>
  }

  const ordered = [...orderEventUpdates].sort((a, b) =>
    sortOrder === 'oldest' ? compareAscending(dateKind, a, b) : compareAscending(dateKind, b, a)
  )

  return (
    <>
      <div className="flex items-center justify-between gap-4">
        <h3 className="text-sm font-medium text-foreground inline-flex items-center">
          <span>History</span>
        </h3>
        <div role="group" aria-label="Date" className="inline-flex gap-0.5 rounded-lg bg-muted p-0.5">
          {DATE_KIND_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              title={option.title}
              aria-pressed={dateKind === option.value}
              onClick={() => setDateKind(option.value)}
              className={cn(
                'h-7 rounded-md px-3 text-xs font-medium',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                dateKind === option.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      <ul role="list" className="space-y-6">
        {ordered.map((update: OrderEventUpdate, idx: number, updates: OrderEventUpdate[]) => {
          const documentChange = documentChangeLabel(update)
          const body = (
            <>
              <div className="flex gap-x-4">
                {idx < updates.length - 1 && (
                  <div className="absolute top-0 -bottom-6 left-0 flex w-6 justify-center">
                    <div className="w-px bg-border"></div>
                  </div>
                )}
                <div className="relative flex size-6 flex-none items-center justify-center bg-background">
                  <div className="size-1.5 rounded-full bg-muted ring-1 ring-border"></div>
                </div>
                <p className="flex-auto py-0.5 text-xs/5 text-muted-foreground">
                  <span className="font-medium text-foreground">
                    {update.createdBy?.firstName} {update.createdBy?.lastName}
                  </span>{' '}
                  {getPastTense(update.type)} {changeCase.sentenceCase(orderEventTypeLabel(update.targetEventType))}{' '}
                  {update.targetEventIdDisplay}
                  {documentChange ? <> — {documentChange}</> : null}
                </p>
                <div className="flex-none py-0.5 text-xs/5 text-muted-foreground text-right">
                  {dateKind === 'effective' ? (
                    dateFormattedOrDash(update.effectiveDate)
                  ) : (
                    <time dateTime={update.createdAt}>
                      {parseTimestamp(update.createdAt)?.toKlearDate().toString() ?? '-'}
                    </time>
                  )}
                </div>
              </div>
              {update.notes ? (
                <p className="text-xs text-muted-foreground whitespace-pre-wrap mt-2 ml-14">{update.notes}</p>
              ) : null}
            </>
          )

          return (
            <li key={update.id} className="relative">
              {onSelectEvent ? (
                <button
                  type="button"
                  onClick={() => onSelectEvent(update.targetEventId)}
                  className="block w-full text-left rounded-md -mx-2 px-2 py-1 hover:bg-muted/60 transition-colors"
                >
                  {body}
                </button>
              ) : (
                body
              )}
            </li>
          )
        })}
      </ul>
    </>
  )
}
